import React, { useEffect, useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { infoSessionService } from '../services/api';
import { InfoSession, InfoSessionReminderLog, InfoSessionRegistrationAdmin } from '../types';
import { PlusIcon } from '@heroicons/react/24/outline';

const emptyForm = {
  title: '',
  description: '',
  starts_at: '',
  duration_minutes: 45,
  timezone: 'America/New_York',
  join_url: '',
  capacity: '',
  host_name: '',
  host_email: '',
  is_active: true,
  reminders_enabled: true,
  reminder_offsets: '1440,60',
  reminder_channels: {
    email: true,
    sms: true,
  },
};

const InfoSessionsManagement: React.FC = () => {
  const { user } = useAuth();
  const [sessions, setSessions] = useState<InfoSession[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ ...emptyForm });
  const [editing, setEditing] = useState<InfoSession | null>(null);
  const [reminderLogs, setReminderLogs] = useState<InfoSessionReminderLog[]>([]);
  const [reminderLoading, setReminderLoading] = useState(false);
  const [testReminderStatus, setTestReminderStatus] = useState<string | null>(null);
  const [expandedSessionId, setExpandedSessionId] = useState<number | null>(null);
  const [registrationsBySession, setRegistrationsBySession] = useState<Record<number, InfoSessionRegistrationAdmin[]>>({});
  const [registrationsLoadingId, setRegistrationsLoadingId] = useState<number | null>(null);

  useEffect(() => {
    if (user?.is_admin) fetchSessions();
  }, [user]);

  const fetchSessions = async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await infoSessionService.listAdmin();
      setSessions(data);
    } catch {
      setError('Failed to load info sessions');
    } finally {
      setLoading(false);
    }
  };

  const handleInput = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value, type } = e.target;
    setForm((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? (e.target as HTMLInputElement).checked : value,
    }));
  };

  const handleReminderChannel = (channel: 'email' | 'sms') => {
    setForm((prev) => ({
      ...prev,
      reminder_channels: {
        ...prev.reminder_channels,
        [channel]: !prev.reminder_channels[channel],
      },
    }));
  };

  const resetForm = () => {
    setForm({ ...emptyForm });
    setEditing(null);
    setShowForm(false);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    if (!form.starts_at) {
      setError('Start time is required');
      return;
    }
    const startsAtDate = new Date(form.starts_at);
    if (Number.isNaN(startsAtDate.getTime())) {
      setError('Invalid start time');
      return;
    }
    try {
      const offsets = form.reminder_offsets
        .split(',')
        .map((value) => Number(value.trim()))
        .filter((value) => Number.isFinite(value) && value > 0);
      const channels = Object.entries(form.reminder_channels)
        .filter(([, enabled]) => enabled)
        .map(([key]) => key);
      const payload = {
        title: form.title.trim(),
        description: form.description.trim() || undefined,
        starts_at: startsAtDate.toISOString(),
        duration_minutes: Number(form.duration_minutes) || 45,
        timezone: form.timezone.trim() || 'UTC',
        join_url: form.join_url.trim(),
        capacity: form.capacity ? Number(form.capacity) : undefined,
        is_active: form.is_active,
        reminders_enabled: form.reminders_enabled,
        reminder_offsets: offsets,
        reminder_channels: channels,
        host_name: form.host_name.trim() || undefined,
        host_email: form.host_email.trim() || undefined,
      };
      if (editing) {
        await infoSessionService.update(editing.id, payload);
      } else {
        await infoSessionService.create(payload);
      }
      await fetchSessions();
      await fetchReminderLogs();
      resetForm();
    } catch (err: any) {
      setError(err?.response?.data?.error || 'Failed to create info session');
    }
  };

  const handleEdit = (session: InfoSession) => {
    const startsAtLocal = new Date(session.starts_at);
    const localValue = startsAtLocal.toISOString().slice(0, 16);
    setEditing(session);
    setForm({
      title: session.title,
      description: session.description || '',
      starts_at: localValue,
      duration_minutes: session.duration_minutes,
      timezone: session.timezone || 'UTC',
      join_url: session.join_url,
      capacity: session.capacity ? String(session.capacity) : '',
      host_name: session.host_name || '',
      host_email: session.host_email || '',
      is_active: session.is_active,
      reminders_enabled: session.reminders_enabled ?? true,
      reminder_offsets: (session.reminder_offsets || []).join(','),
      reminder_channels: {
        email: session.reminder_channels?.includes('email') ?? true,
        sms: session.reminder_channels?.includes('sms') ?? true,
      },
    });
    setShowForm(true);
  };

  const handleDelete = async (session: InfoSession) => {
    if (!window.confirm(`Delete "${session.title}"? This cannot be undone.`)) return;
    try {
      await infoSessionService.delete(session.id);
      await fetchSessions();
      await fetchReminderLogs();
    } catch (err: any) {
      setError(err?.response?.data?.error || 'Failed to delete info session');
    }
  };

  const handleSendTestReminder = async (session: InfoSession, channel: 'email' | 'sms') => {
    setTestReminderStatus(null);
    try {
      const response = await infoSessionService.sendTestReminder(session.id, channel);
      setTestReminderStatus(response.message || `Test ${channel} sent`);
      await fetchReminderLogs();
    } catch (err: any) {
      setTestReminderStatus(err?.response?.data?.error || `Test ${channel} failed`);
    }
  };

  const fetchReminderLogs = async () => {
    setReminderLoading(true);
    try {
      const data = await infoSessionService.listReminderLogs(100);
      setReminderLogs(data);
    } catch {
      setReminderLogs([]);
    } finally {
      setReminderLoading(false);
    }
  };

  const handleToggleRegistrations = async (session: InfoSession) => {
    if (expandedSessionId === session.id) {
      setExpandedSessionId(null);
      return;
    }
    setExpandedSessionId(session.id);
    if (registrationsBySession[session.id]) return;
    setRegistrationsLoadingId(session.id);
    try {
      const regs = await infoSessionService.listRegistrationsAdmin(session.id);
      setRegistrationsBySession((prev) => ({ ...prev, [session.id]: regs }));
    } catch {
      setRegistrationsBySession((prev) => ({ ...prev, [session.id]: [] }));
    } finally {
      setRegistrationsLoadingId(null);
    }
  };

  const formatRegistrantName = (reg: InfoSessionRegistrationAdmin) => {
    const full = `${reg.first_name || ''} ${reg.last_name || ''}`.trim();
    return full || reg.email || 'Unknown';
  };

  const formatDate = (session: InfoSession) => {
    const date = new Date(session.starts_at);
    try {
      return new Intl.DateTimeFormat('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
        hour: 'numeric',
        minute: '2-digit',
        timeZone: session.timezone || undefined,
        timeZoneName: 'short',
      }).format(date);
    } catch {
      return date.toLocaleString();
    }
  };

  useEffect(() => {
    if (user?.is_admin) {
      fetchReminderLogs();
    }
  }, [user]);

  if (!user?.is_admin) return <div className="text-center py-20 text-red-400">Admin access required.</div>;

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-3xl font-bold text-gray-900 dark:text-white">Info Sessions</h1>
        <p className="text-sm text-gray-600 dark:text-slate-300 mt-1">Schedule, update, and review session attendance.</p>
      </div>
      {error && <div className="text-red-500 mb-4">{error}</div>}
      <button
        className="inline-flex items-center justify-center gap-2 font-semibold rounded-lg transition-colors bg-emerald-600 text-white border border-emerald-600 hover:bg-emerald-700 hover:border-emerald-700 px-4 py-2 w-full sm:w-auto"
        onClick={() => {
          setEditing(null);
          setForm({ ...emptyForm });
          setShowForm(true);
        }}
      >
        <PlusIcon className="w-5 h-5 mr-2" /> Schedule Session
      </button>
      {showForm && (
        <form className="bg-white/85 dark:bg-slate-900/70 border border-slate-100/90 dark:border-slate-700/80 rounded-3xl shadow-[0_14px_36px_rgba(15,23,42,0.12)] dark:shadow-[0_18px_40px_rgba(0,0,0,0.55)] backdrop-blur-[18px] overflow-hidden mb-8" onSubmit={handleSubmit}>
          <div className="px-6 py-4 border-b border-slate-100/80 dark:border-slate-700/80 flex items-center justify-between">
            <h3 className="text-lg font-medium text-slate-900 dark:text-slate-50">{editing ? 'Edit session' : 'Schedule session'}</h3>
          </div>
          <div className="p-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium mb-1">Title</label>
              <input
                name="title"
                value={form.title}
                onChange={handleInput}
                className="w-full border rounded px-3 py-2"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Start Time</label>
              <input
                type="datetime-local"
                name="starts_at"
                value={form.starts_at}
                onChange={handleInput}
                className="w-full border rounded px-3 py-2"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Duration (minutes)</label>
              <input
                type="number"
                name="duration_minutes"
                value={form.duration_minutes}
                onChange={handleInput}
                className="w-full border rounded px-3 py-2"
                min={15}
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Timezone</label>
              <input
                name="timezone"
                value={form.timezone}
                onChange={handleInput}
                className="w-full border rounded px-3 py-2"
                placeholder="America/New_York"
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Join URL</label>
              <input
                name="join_url"
                value={form.join_url}
                onChange={handleInput}
                className="w-full border rounded px-3 py-2"
                placeholder="https://zoom.us/j/..."
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Capacity (optional)</label>
              <input
                type="number"
                name="capacity"
                value={form.capacity}
                onChange={handleInput}
                className="w-full border rounded px-3 py-2"
                min={1}
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Host Name</label>
              <input
                name="host_name"
                value={form.host_name}
                onChange={handleInput}
                className="w-full border rounded px-3 py-2"
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Host Email</label>
              <input
                type="email"
                name="host_email"
                value={form.host_email}
                onChange={handleInput}
                className="w-full border rounded px-3 py-2"
              />
            </div>
          </div>
          <div className="mt-4 grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium mb-1">Reminder Offsets (minutes)</label>
              <input
                name="reminder_offsets"
                value={form.reminder_offsets}
                onChange={handleInput}
                className="w-full border rounded px-3 py-2"
                placeholder="1440,60"
              />
              <p className="text-xs text-gray-400 mt-1">Comma separated (e.g., 1440,60)</p>
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Reminder Channels</label>
              <div className="flex items-center gap-4">
                <label className="inline-flex items-center text-sm">
                  <input
                    type="checkbox"
                    checked={form.reminder_channels.email}
                    onChange={() => handleReminderChannel('email')}
                    className="mr-2"
                  />
                  Email
                </label>
                <label className="inline-flex items-center text-sm">
                  <input
                    type="checkbox"
                    checked={form.reminder_channels.sms}
                    onChange={() => handleReminderChannel('sms')}
                    className="mr-2"
                  />
                  SMS
                </label>
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Reminders Enabled</label>
              <label className="inline-flex items-center text-sm">
                <input
                  type="checkbox"
                  name="reminders_enabled"
                  checked={form.reminders_enabled}
                  onChange={handleInput}
                  className="mr-2"
                />
                Send reminders
              </label>
            </div>
          </div>
          <div className="mt-4">
            <label className="inline-flex items-center text-sm">
              <input
                type="checkbox"
                name="is_active"
                checked={form.is_active}
                onChange={handleInput}
                className="mr-2"
              />
              Active
            </label>
          </div>
          <div className="mt-6 flex flex-col sm:flex-row gap-3">
            <button type="submit" className="inline-flex items-center justify-center gap-2 font-semibold rounded-lg transition-colors bg-emerald-600 text-white border border-emerald-600 hover:bg-emerald-700 hover:border-emerald-700 px-4 py-2 w-full sm:w-auto">
              {editing ? 'Update Session' : 'Save Session'}
            </button>
            <button type="button" onClick={resetForm} className="inline-flex items-center justify-center gap-2 font-semibold rounded-lg transition-colors bg-white/80 dark:bg-slate-900/60 border border-slate-300 dark:border-slate-700 text-slate-700 dark:text-slate-200 hover:bg-slate-50 dark:hover:bg-slate-800 px-4 py-2 w-full sm:w-auto">
              Cancel
            </button>
          </div>
          </div>
        </form>
      )}

      <div className="bg-white/85 dark:bg-slate-900/70 border border-slate-100/90 dark:border-slate-700/80 rounded-3xl shadow-[0_14px_36px_rgba(15,23,42,0.12)] dark:shadow-[0_18px_40px_rgba(0,0,0,0.55)] backdrop-blur-[18px] overflow-hidden">
        <div className="px-6 py-4 border-b border-slate-100/80 dark:border-slate-700/80 flex items-center justify-between">
          <h3 className="text-lg font-medium text-slate-900 dark:text-slate-50">All sessions</h3>
        </div>
        {testReminderStatus && (
          <div className="px-6 py-3 text-sm text-emerald-700 bg-emerald-50 border-b border-emerald-100">
            {testReminderStatus}
          </div>
        )}
        {loading ? (
          <div className="px-6 py-6 text-gray-500">Loading sessions...</div>
        ) : sessions.length === 0 ? (
          <div className="px-6 py-6 text-gray-500">No sessions scheduled.</div>
        ) : (
          <div className="divide-y">
            {sessions.map((session) => (
              <div key={session.id} className="px-6 py-4">
                <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
                  <div>
                    <p className="text-sm font-semibold text-gray-900">{session.title}</p>
                    <p className="text-xs text-gray-500">{formatDate(session)}</p>
                    {session.description && <p className="text-xs text-gray-500 mt-1">{session.description}</p>}
                  </div>
                  <div className="text-xs text-gray-500 space-y-1">
                    <div>Status: {session.is_active ? 'Active' : 'Inactive'}</div>
                    <div>Duration: {session.duration_minutes} min</div>
                    {session.capacity ? <div>Capacity: {session.capacity}</div> : <div>Capacity: Open</div>}
                    <div>Reminders: {session.reminders_enabled ? (session.reminder_offsets || []).join(', ') || 'On' : 'Off'}</div>
                  </div>
                  <div className="flex flex-col sm:flex-row sm:items-center gap-2 flex-wrap">
                    <button
                      type="button"
                      onClick={() => handleToggleRegistrations(session)}
                      className="px-3 py-2 text-xs font-medium rounded-md border border-amber-200 text-amber-700 hover:bg-amber-50 w-full sm:w-auto"
                    >
                      {expandedSessionId === session.id ? 'Hide Registrations' : 'View Registrations'}
                    </button>
                    <button
                      type="button"
                      onClick={() => handleEdit(session)}
                      className="px-3 py-2 text-xs font-medium rounded-md border border-slate-200 text-slate-700 hover:bg-slate-50 w-full sm:w-auto"
                    >
                      Edit
                    </button>
                    <button
                      type="button"
                      onClick={() => infoSessionService.update(session.id, { is_active: !session.is_active }).then(fetchSessions)}
                      className="px-3 py-2 text-xs font-medium rounded-md border border-emerald-200 text-emerald-700 hover:bg-emerald-50 w-full sm:w-auto"
                    >
                      {session.is_active ? 'Deactivate' : 'Activate'}
                    </button>
                    <button
                      type="button"
                      onClick={() => handleSendTestReminder(session, 'email')}
                      className="px-3 py-2 text-xs font-medium rounded-md border border-blue-200 text-blue-700 hover:bg-blue-50 w-full sm:w-auto"
                    >
                      Test Email
                    </button>
                    <button
                      type="button"
                      onClick={() => handleSendTestReminder(session, 'sms')}
                      className="px-3 py-2 text-xs font-medium rounded-md border border-indigo-200 text-indigo-700 hover:bg-indigo-50 w-full sm:w-auto"
                    >
                      Test SMS
                    </button>
                    <button
                      type="button"
                      onClick={() => handleDelete(session)}
                      className="px-3 py-2 text-xs font-medium rounded-md border border-red-200 text-red-600 hover:bg-red-50 w-full sm:w-auto"
                    >
                      Delete
                    </button>
                  </div>
                </div>
                {expandedSessionId === session.id && (
                  <div className="mt-4 border border-amber-100 rounded-lg bg-amber-50/40">
                    <div className="px-4 py-2 border-b border-amber-100 text-xs font-semibold text-amber-800">
                      Registered Users
                    </div>
                    {registrationsLoadingId === session.id ? (
                      <div className="px-4 py-3 text-xs text-gray-500">Loading registrations...</div>
                    ) : (registrationsBySession[session.id] || []).length === 0 ? (
                      <div className="px-4 py-3 text-xs text-gray-500">No registrations yet.</div>
                    ) : (
                      <div className="divide-y divide-amber-100">
                        {(registrationsBySession[session.id] || []).map((reg) => (
                          <div key={reg.registration_id} className="px-4 py-3 flex flex-col md:flex-row md:items-center md:justify-between gap-2">
                            <div>
                              <div className="text-sm font-medium text-gray-900">{formatRegistrantName(reg)}</div>
                              <div className="text-xs text-gray-500">{reg.email || reg.phone || 'No contact info'}</div>
                            </div>
                            <div className="text-xs text-gray-500 space-y-1">
                              <div>Role: {reg.role || 'student'}</div>
                              <div>Registered: {reg.registered_at ? new Date(reg.registered_at).toLocaleString() : '—'}</div>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="bg-white/85 dark:bg-slate-900/70 border border-slate-100/90 dark:border-slate-700/80 rounded-3xl shadow-[0_14px_36px_rgba(15,23,42,0.12)] dark:shadow-[0_18px_40px_rgba(0,0,0,0.55)] backdrop-blur-[18px] overflow-hidden mt-10">
        <div className="px-6 py-4 border-b border-slate-100/80 dark:border-slate-700/80 flex items-center justify-between">
          <h3 className="text-lg font-medium text-slate-900 dark:text-slate-50">Reminder history</h3>
          <button
            type="button"
            onClick={fetchReminderLogs}
            className="text-xs font-medium text-emerald-600 hover:text-emerald-700"
          >
            Refresh
          </button>
        </div>
        {reminderLoading ? (
          <div className="px-6 py-6 text-gray-500">Loading reminders...</div>
        ) : reminderLogs.length === 0 ? (
          <div className="px-6 py-6 text-gray-500">No reminders sent yet.</div>
        ) : (
          <div className="divide-y">
            {reminderLogs.map((log) => (
              <div key={log.id} className="px-6 py-4 flex flex-col lg:flex-row lg:items-center lg:justify-between gap-3">
                <div>
                  <p className="text-sm font-semibold text-gray-900">{log.session_title || 'Session'}</p>
                  <p className="text-xs text-gray-500">
                    {log.session_starts_at ? new Date(log.session_starts_at).toLocaleString() : 'Unknown time'}
                  </p>
                  <p className="text-xs text-gray-500">
                    {log.prospect_name || 'Student'} · {log.prospect_email || log.prospect_phone || 'No contact'}
                  </p>
                </div>
                <div className="text-xs text-gray-500 space-y-1">
                  <div>Channel: {log.channel}</div>
                  <div>Offset: {log.offset_minutes} min</div>
                  <div>Status: {log.status}</div>
                  {log.error && <div className="text-red-500">Error: {log.error}</div>}
                </div>
                <div className="text-xs text-gray-500">
                  Sent: {log.sent_at ? new Date(log.sent_at).toLocaleString() : '—'}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default InfoSessionsManagement;
